import { useI18n } from '@cascade/react/i18n';
import { useProjects } from '@cascade/react/projects';
import { getDisplayName } from '@cascade/core/auth';
import { useAuthentication } from '@cascade/react/authentication';
import { Avatar, Icon, Menu, Text } from '@cascade/ui-library';
import type { TMenuEntry } from '@cascade/ui-library';
import clsx from 'clsx';
import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PoweredBy } from './powered-by/index.ts';
import classes from './UserArea.module.scss';
import {
  DATA_CONTROLS_PATH,
  DEPENDENTS_PATH,
  SETTINGS_PATH,
} from '#config/index.ts';
import { useLayout } from '#layout/Layout.context.ts';

export const UserArea = () => {
  const { translate } = useI18n();
  const { user, signOut } = useAuthentication();
  const { settings, canManageDependents } = useProjects();
  const { isRail } = useLayout();
  const navigate = useNavigate();
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const displayName = getDisplayName(user);
  const hasProjectLogo = Boolean(settings?.project_theme?.logo_url);
  const menuLabel = translate('user.menu', { name: displayName });

  const closeMenu = () => setIsMenuOpen(false);
  const toggleMenu = () => setIsMenuOpen((isOpen) => !isOpen);
  const openSettings = () => navigate(`/${SETTINGS_PATH}`);
  const openDataControls = () => navigate(`/${DATA_CONTROLS_PATH}`);
  const openDependents = () => navigate(`/${DEPENDENTS_PATH}`);

  const settingsEntry: TMenuEntry = {
    id: 'settings',
    label: translate('settings.title'),
    iconType: 'gear',
    onClick: openSettings,
  };

  const dataControlsEntry: TMenuEntry = {
    id: 'data-controls',
    label: translate('dataControls.title'),
    iconType: 'user-shield',
    onClick: openDataControls,
  };

  const dependentsEntry: TMenuEntry = {
    id: 'dependents',
    label: translate('dependents.title'),
    iconType: 'users',
    onClick: openDependents,
  };

  const signOutEntry: TMenuEntry = {
    id: 'sign-out',
    label: translate('session.signOut'),
    iconType: 'log-out',
    onClick: signOut,
  };

  const items = [
    settingsEntry,
    dataControlsEntry,
    ...(canManageDependents ? [dependentsEntry] : []),
    signOutEntry,
  ];

  return (
    <div className={clsx(classes['user-area'], { [classes.rail]: isRail })}>
      <button
        type='button'
        ref={triggerRef}
        className={classes.trigger}
        aria-label={menuLabel}
        aria-haspopup='menu'
        aria-expanded={isMenuOpen}
        aria-controls={menuId}
        onClick={toggleMenu}
      >
        <Avatar name={displayName} size='sm' />
        {isRail ? null : (
          <>
            <Text
              type='body-2'
              weight='semibold'
              content={displayName}
              truncated
            />
            <Icon
              type='dropdown-arrow'
              size='xs'
              className={clsx(classes.arrow, { [classes.open]: isMenuOpen })}
            />
          </>
        )}
      </button>
      <Menu
        id={menuId}
        items={items}
        triggerRef={triggerRef}
        open={isMenuOpen}
        matchTriggerWidth={!isRail}
        aria-label={menuLabel}
        onClose={closeMenu}
      />
      {hasProjectLogo ? <PoweredBy isRail={isRail} /> : null}
    </div>
  );
};
